/**
 * Client API – endpoints under /api/users/ that the public client
 * setup flow hits. The trainer-branded `/client-setup/:token` page
 * uses these two calls to render and submit; neither sends an
 * `x-auth-token` header because the (token, email) bind is the auth
 * signal during invite redemption.
 */

const getApiBase = () => {
  const host = process.env.REACT_APP_API_URL;
  if (host) return `${host}/api`;
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    window.location.port === '3000'
  ) {
    return 'http://localhost:5000/api';
  }
  return '/api';
};

/**
 * Public — fetch the trainer-brand context for the client-setup
 * landing page. Backend requires (token, email) to both match the
 * persisted invite; if either is wrong it returns a generic 404 so
 * we surface the same "invalid or expired" message either way.
 *
 * Backend: GET /api/users/client-invite/:token?email=…
 *
 * @returns {Promise<{ client: { firstName: string }, brand: { name, colors, logo, source } }>}
 */
export const fetchClientInviteContext = async (token, email) => {
  const url = `${getApiBase()}/users/client-invite/${encodeURIComponent(
    token,
  )}?email=${encodeURIComponent(email)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const err = new Error(res.statusText || 'Could not load invite');
    err.status = res.status;
    err.body = await res.json().catch(() => ({}));
    throw err;
  }
  return res.json();
};

/**
 * Public — redeem a client invite. Server consumes the token, marks
 * the email verified, and returns a Firebase custom token so the
 * page can sign the client in immediately. NO password is set here
 * — the canonical "pick your password" step is the final question
 * of the assessment (`restPassword`); this endpoint just unlocks
 * the path into that assessment.
 *
 * Backend: POST /api/users/accept-client-invite
 *
 * @param {Object} payload  - { token, email }
 * @returns {Promise<{customToken: string|null, user: object}>}
 */
export const acceptClientInvite = async (payload) => {
  const url = `${getApiBase()}/users/accept-client-invite`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = new Error(res.statusText || 'Could not activate account');
    err.status = res.status;
    err.body = await res.json().catch(() => ({}));
    throw err;
  }
  return res.json();
};
